import MainLayout from '@/components/layout/MainLayout';
import { Link } from 'react-router-dom';

const reportCategories = [
  {
    category: 'Contratos',
    reports: [
      {
        title: 'Contratos por Fecha',
        description: 'Consulta contratos creados o activos en un período específico',
        path: '/reports/contracts-by-date',
        icon: '📅',
        color: 'blue'
      },
      {
        title: 'Contratos por Mes',
        description: 'Resumen mensual de contratos activos y finalizados',
        path: '/reports/contracts-by-month',
        icon: '📊',
        color: 'blue'
      },
      {
        title: 'Contratos para Reajuste',
        description: 'Previsualización de los contratos que el cron renovará y reajustará en el mes actual y el próximo',
        path: '/reports/adjustments',
        icon: '💹',
        color: 'blue'
      }
    ]
  },
  {
    category: 'Pagos y Recibos',
    reports: [
      {
        title: 'Recibos por Fecha - Inquilinos',
        description: 'Recibos de inquilinos generados en un período específico',
        path: '/reports/receipts-tenants',
        icon: '🧾',
        color: 'purple'
      },
      {
        title: 'Recibos por Fecha - Propietarios',
        description: 'Recibos de propietarios generados en un período específico',
        path: '/reports/receipts-owners',
        icon: '📄',
        color: 'purple'
      },
      {
        title: 'Cancelados y Emitidos',
        description: 'Estado de pagos cancelados y emitidos por período',
        path: '/reports/cancelled-issued',
        icon: '✅',
        color: 'teal'
      }
    ]
  },
  {
    category: 'Alquileres',
    reports: [
      {
        title: 'Alquileres Atrasados - Inquilinos',
        description: 'Pagos de inquilinos pendientes y vencidos',
        path: '/reports/overdue-tenants',
        icon: '⚠️',
        color: 'red'
      },
      {
        title: 'Alquileres Atrasados - Propietarios',
        description: 'Pagos a propietarios pendientes y vencidos',
        path: '/reports/overdue-owners',
        icon: '🔴',
        color: 'red'
      },
      {
        title: 'Alquileres Adelantados - Inquilinos',
        description: 'Pagos de inquilinos realizados anticipadamente',
        path: '/reports/advance-tenants',
        icon: '💚',
        color: 'green'
      },
      {
        title: 'Alquileres Adelantados - Propietarios',
        description: 'Pagos a propietarios realizados anticipadamente',
        path: '/reports/advance-owners',
        icon: '✅',
        color: 'green'
      }
    ]
  },
  {
    category: 'Clientes e Inmuebles',
    reports: [
      {
        title: 'Reporte de Clientes',
        description: 'Listado completo de clientes con filtros y estadísticas',
        path: '/reports/clients',
        icon: '👥',
        color: 'indigo'
      },
      {
        title: 'Reporte de Inmuebles',
        description: 'Listado de propiedades con estado y ocupación',
        path: '/reports/properties',
        icon: '🏢',
        color: 'cyan'
      },
      {
        title: 'Inmuebles Entregados por Mes',
        description: 'Historial mensual de propiedades entregadas a inquilinos',
        path: '/reports/properties-delivered',
        icon: '🏠',
        color: 'green'
      }
    ]
  },
  {
    category: 'Legal',
    reports: [
      {
        title: 'Lista Jurídica',
        description: 'Casos con morosidad significativa para acción legal',
        path: '/reports/legal-list',
        icon: '⚖️',
        color: 'orange'
      }
    ]
  }
];

const colorClasses = {
  red: 'bg-red-50 border-red-200 hover:border-red-300 hover:shadow-red-100',
  blue: 'bg-blue-50 border-blue-200 hover:border-blue-300 hover:shadow-blue-100',
  green: 'bg-green-50 border-green-200 hover:border-green-300 hover:shadow-green-100',
  purple: 'bg-purple-50 border-purple-200 hover:border-purple-300 hover:shadow-purple-100',
  orange: 'bg-orange-50 border-orange-200 hover:border-orange-300 hover:shadow-orange-100',
  teal: 'bg-teal-50 border-teal-200 hover:border-teal-300 hover:shadow-teal-100',
  indigo: 'bg-indigo-50 border-indigo-200 hover:border-indigo-300 hover:shadow-indigo-100',
  cyan: 'bg-cyan-50 border-cyan-200 hover:border-cyan-300 hover:shadow-cyan-100'
};

const iconColorClasses = {
  red: 'text-red-600',
  blue: 'text-blue-600',
  green: 'text-green-600',
  purple: 'text-purple-600',
  orange: 'text-orange-600',
  teal: 'text-teal-600',
  indigo: 'text-indigo-600',
  cyan: 'text-cyan-600'
};

export default function ReportsDashboard() {
  return (
    <MainLayout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Reportes</h1>
          <p className="text-gray-600">Accede a informes y estadísticas del sistema organizados por categoría</p>
        </div>

        <div className="space-y-10">
          {reportCategories.map((category) => (
            <div key={category.category}>
              <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">
                {category.category}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {category.reports.map((report) => (
                  <Link
                    key={report.path}
                    to={report.path}
                    className={`block p-5 border-2 rounded-lg transition-all duration-200 hover:shadow-lg ${colorClasses[report.color as keyof typeof colorClasses]}`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="text-3xl">{report.icon}</div>
                      <div className="flex-1">
                        <h3 className={`text-base font-semibold mb-1 ${iconColorClasses[report.color as keyof typeof iconColorClasses]}`}>
                          {report.title}
                        </h3>
                        <p className="text-sm text-gray-600 leading-relaxed">
                          {report.description}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center text-xs font-medium text-gray-500">
                      <span>Ver reporte</span>
                      <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </MainLayout>
  );
}



